import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  dashboardService,
  seatService,
  type DashboardOverview,
  type ActivityItem,
  type ClientSummary,
} from '../../services'
import { useAuthStore } from '../../stores'
import { LoadingScreen, UsageCard } from '../../components/common'
import {
  QuickActionCard,
  QuickActionIcons,
  AtAGlanceStats,
  RecentActivityFeed,
} from '../../components/dashboard'

export function DashboardPage() {
  const { profile, canUploadVideos } = useAuthStore()
  const [overview, setOverview] = useState<DashboardOverview | null>(null)
  const [activities, setActivities] = useState<ActivityItem[]>([])
  const [clientUsage, setClientUsage] = useState<ClientSummary | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    // Timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (isLoading) {
        setIsLoading(false)
      }
    }, 10000)

    const loadDashboardData = async () => {
      if (!profile) return

      try {
        // Load all dashboard data in parallel
        const [overviewData, activityData, clientSummary] = await Promise.all([
          dashboardService.getDashboardOverview(profile.id),
          dashboardService.getRecentActivity(profile.id, 5),
          seatService.getProviderClientSummary(profile.id),
        ])

        setOverview(overviewData)
        setActivities(activityData)
        setClientUsage(clientSummary)
      } catch (error) {
        console.error('[DashboardPage] Failed to load dashboard data:', error)
      } finally {
        setIsLoading(false)
      }
    }

    loadDashboardData()

    return () => clearTimeout(timeout)
  }, [profile, isLoading])

  if (isLoading) {
    return <LoadingScreen message="Loading dashboard..." />
  }

  return (
    <div className="space-y-8">
      {/* Welcome header with quick stats */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Welcome back, {profile?.full_name || 'Coach'}!
          </h1>
          <p className="mt-1 text-gray-600">
            Here's what's happening with your clients today.
          </p>
        </div>
        {overview && (
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              {overview.totalViews.toLocaleString()} views
            </span>
            <span className="flex items-center gap-1">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
              {overview.totalLikes.toLocaleString()} likes
            </span>
          </div>
        )}
      </div>

      {/* 4 Quick Action Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <QuickActionCard
          icon={QuickActionIcons.buildProgram}
          label="Build Program"
          to="/dashboard/programs/new"
          color="blue"
          isPrimary
        />
        <QuickActionCard
          icon={QuickActionIcons.activePrograms}
          label="Active Programs"
          count={overview?.activePrograms || 0}
          to="/dashboard/assignments?status=active"
          color="green"
        />
        <QuickActionCard
          icon={QuickActionIcons.completedPrograms}
          label="Completed"
          count={overview?.completedPrograms || 0}
          to="/dashboard/assignments?status=completed"
          color="purple"
        />
        <QuickActionCard
          icon={QuickActionIcons.videoLibrary}
          label="Video Library"
          count={overview?.totalVideos || 0}
          to="/dashboard/videos"
          color="orange"
        />
      </div>

      {/* Main content area: At-a-Glance + Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AtAGlanceStats
          activeClients={overview?.activeClients || 0}
          programsInProgress={overview?.programsInProgress || 0}
          overallCompletionRate={overview?.overallCompletionRate || 0}
          needsAttention={overview?.needsAttention || 0}
          isLoading={isLoading}
        />
        <RecentActivityFeed
          activities={activities}
          isLoading={isLoading}
          maxItems={5}
        />
      </div>

      {/* Plan Usage */}
      {clientUsage && clientUsage.max_clients !== 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Plan Usage
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <UsageCard
              label="Clients"
              current={clientUsage.current_clients}
              max={clientUsage.max_clients}
              icon={
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              }
            />
          </div>
        </div>
      )}

      {/* Quick actions (secondary) */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Quick Actions
        </h2>
        <div className="flex flex-wrap gap-3">
          {canUploadVideos() ? (
            <Link
              to="/dashboard/videos/upload"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                />
              </svg>
              Upload Video
            </Link>
          ) : (
            <div className="flex items-center px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800">
                Upgrade to Advanced to upload videos.{' '}
                <Link
                  to="/dashboard/subscription"
                  className="font-medium underline"
                >
                  Upgrade
                </Link>
              </p>
            </div>
          )}
          <Link
            to="/dashboard/clients"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
              />
            </svg>
            Add Client
          </Link>
          <Link
            to="/dashboard/programs"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 10h16M4 14h16M4 18h16"
              />
            </svg>
            View Programs
          </Link>
          <Link
            to="/dashboard/profile"
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
              />
            </svg>
            Edit Profile
          </Link>
        </div>
      </div>

      {/* Getting started (for new users) */}
      {overview?.totalVideos === 0 && overview?.activePrograms === 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-6">
          <h2 className="text-lg font-semibold text-blue-900 mb-2">
            Getting Started
          </h2>
          <p className="text-blue-700 mb-4">
            Welcome to ExerciseToolbox! Here's how to get started:
          </p>
          <ol className="list-decimal list-inside space-y-2 text-blue-700">
            <li>Complete your profile to build trust with clients</li>
            <li>Upload your first exercise video or browse the public library</li>
            <li>Create a program and add videos to it</li>
            <li>Assign the program to your clients</li>
            <li>Track their progress and engagement</li>
          </ol>
          <div className="mt-4 flex gap-3">
            <Link
              to="/dashboard/profile"
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Complete Profile
            </Link>
            <Link
              to="/dashboard/programs/new"
              className="inline-flex items-center px-4 py-2 bg-white text-blue-600 border border-blue-300 rounded-lg hover:bg-blue-50 transition-colors text-sm font-medium"
            >
              Create First Program
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
